/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../../../common/constants';
import {
  normalizeBatchReportResponse,
  normalizePaginatedScoresPage,
  normalizeScoreTextContent
} from './evalExecutionReportUtils';

const evalExecutionListRow = types.model('EvalExecutionListRow', {
  metadata: types.optional(types.frozen(), {})
});

const unwrapResponseData = (response) => {
  if (response == null) return null;
  return response?.data ?? response;
};

const buildCacheKey = (tenantId, executionId) => `${tenantId}:${executionId}`;

const EvalResultData = types
  .model('EvalResultData', {
    executionDetail: types.maybeNull(types.frozen()),
    executionList: types.optional(types.array(evalExecutionListRow), []),
    cacheKey: types.maybeNull(types.string),
    executionMeta: types.maybeNull(types.frozen()),
    currentScoreDetail: types.maybeNull(types.frozen()),
    pendingScoresRequest: types.maybeNull(types.frozen()),
    pendingScoreDetailRequest: types.maybeNull(types.frozen()),
    batchReport: types.maybeNull(types.frozen()),
    pendingBatchReportRequest: types.maybeNull(types.frozen())
  })
  .actions((self) => ({
    fetchExecutionScores({ tenantId, executionId, offset, limit, graphVersion }) {
      applySnapshot(self, {
        ...getSnapshot(self),
        pendingScoresRequest: { tenantId, executionId, offset, limit, graphVersion }
      });
      Framework.getEventManager().publish(constants.GET_EVAL_REPORT_EXECUTION, {
        tenantId,
        executionId,
        offset,
        limit,
        ...(graphVersion != null ? { graphVersion } : {})
      });
    },
    fetchScoreDetail({ tenantId, executionId, auditId, requestId }) {
      applySnapshot(self, {
        ...getSnapshot(self),
        pendingScoreDetailRequest: { tenantId, executionId, auditId, requestId },
        currentScoreDetail: null
      });
      Framework.getEventManager().publish(constants.GET_EVAL_REPORT_SCORE_DETAIL, {
        tenantId,
        executionId,
        auditId,
        requestId
      });
    },
    fetchExecutionList({ tenantId, graphId, version, startDate, endDate, rubricName }) {
      Framework.getEventManager().publish(constants.GET_EVAL_REPORT_LIST, {
        tenantId,
        graphId,
        version,
        startDate,
        endDate,
        rubricName
      });
    },
    fetchBatchReport({ tenantId, executionId }) {
      applySnapshot(self, {
        ...getSnapshot(self),
        batchReport: null,
        pendingBatchReportRequest: { tenantId, executionId }
      });
      Framework.getEventManager().publish(constants.GET_EVAL_BATCH_REPORT, {
        tenantId,
        executionId
      });
    },
    failExecutionScoresRequest() {
      applySnapshot(self, { ...getSnapshot(self), pendingScoresRequest: null });
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_EXECUTION);
    },
    failScoreDetailRequest() {
      applySnapshot(self, { ...getSnapshot(self), pendingScoreDetailRequest: null });
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_SCORE_DETAIL);
    },
    failBatchReportRequest() {
      applySnapshot(self, { ...getSnapshot(self), pendingBatchReportRequest: null, batchReport: null });
      Framework.getEventManager().publish(constants.SET_EVAL_BATCH_REPORT);
    },
    setExecutionScoresResponse(response) {
      const pending = self.pendingScoresRequest;
      applySnapshot(self, { ...getSnapshot(self), pendingScoresRequest: null });
      if (!pending) {
        return;
      }
      const normalized = normalizePaginatedScoresPage(unwrapResponseData(response));
      if (!normalized) {
        Framework.getEventManager().publish(constants.SET_EVAL_REPORT_EXECUTION);
        return;
      }

      const cacheKey = buildCacheKey(pending.tenantId, pending.executionId);
      let { executionMeta } = self;
      if (normalized.offset === constants.PAGINATION.OFFSET) {
        executionMeta = {
          executionId: normalized.executionId ?? pending.executionId,
          evalName: normalized.evalName,
          graphVersion: normalized.graphVersion ?? null,
          isConversational: normalized.isConversational === true,
          rubricText: normalized.rubricText,
          reportStats: normalized.reportStats ?? null,
          scoreReferenceInterval: normalized.scoreReferenceInterval,
          scoresCount: normalized.scoresCount ?? 0,
          pageLimit: normalized.limit
        };
      }
      const meta = executionMeta || {};
      applySnapshot(self, {
        ...getSnapshot(self),
        cacheKey,
        executionMeta,
        executionDetail: {
          ...meta,
          offset: normalized.offset,
          limit: normalized.limit,
          scoresCount: meta.scoresCount ?? normalized.scoresCount ?? 0,
          scores: normalized.scores
        }
      });
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_EXECUTION);
    },
    setScoreDetailResponse(response) {
      const pending = self.pendingScoreDetailRequest;
      applySnapshot(self, { ...getSnapshot(self), pendingScoreDetailRequest: null });
      if (!pending) {
        return;
      }
      const data = response == null ? null : normalizeScoreTextContent(response);
      applySnapshot(self, {
        ...getSnapshot(self),
        currentScoreDetail: data ? { auditId: pending.auditId, textContent: data.textContent } : null
      });
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_SCORE_DETAIL);
    },
    setExecutionListResponse(response) {
      const data = unwrapResponseData(response);
      const list = Array.isArray(data) ? data : [];
      const rows = list.map((item) => (item?.metadata ? item : { metadata: item }));
      applySnapshot(self, {
        ...getSnapshot(self),
        executionList: rows
      });
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_LIST);
    },
    setBatchReportResponse(response) {
      const pending = self.pendingBatchReportRequest;
      applySnapshot(self, { ...getSnapshot(self), pendingBatchReportRequest: null });
      if (!pending) {
        return;
      }
      applySnapshot(self, {
        ...getSnapshot(self),
        batchReport: normalizeBatchReportResponse(response)
      });
      Framework.getEventManager().publish(constants.SET_EVAL_BATCH_REPORT);
    },
    clearExecutionList() {
      applySnapshot(self, {
        ...getSnapshot(self),
        executionList: []
      });
      Framework.getEventManager().publish(constants.SET_EVAL_REPORT_LIST);
    },
    clearExecutionDetail() {
      applySnapshot(self, {
        ...getSnapshot(self),
        executionDetail: null,
        cacheKey: null,
        executionMeta: null,
        currentScoreDetail: null,
        pendingScoresRequest: null,
        pendingScoreDetailRequest: null
      });
    },
    clearBatchReport() {
      applySnapshot(self, {
        ...getSnapshot(self),
        batchReport: null,
        pendingBatchReportRequest: null
      });
    }
  }));

export default EvalResultData;
